import * as React from "react";
import { useDispatch, useSelector } from "react-redux";

import { HOLIDAYS } from "@config/testIds/holidays";

import { InputEvent, Label, Radio } from "@ui/Atoms/Form";
import { Margins, TestWrapper, OptionHolder } from "@ui/Atoms";

import { stringToBool } from "@services/StringToBool";
import { dateToCalendarFormat } from "@services/DateFormatter";
import { stringifyFalsies } from "@services/StringifyFalsies";

import { showToast } from "@containers/Toastify";
import {
  RangePicker,
  RangePickerState,
  THEME_VARIANT,
} from "@containers/RangePicker";

import { useBreakpoint } from "@hooks/useBreakpoint";
import { useTranslate } from "@hooks/useTranslate";
import { useLocalisation } from "@hooks/useLocalisation";

import { OPTIONS_MODALS } from "@app/pages/ClientDashboard/OptionsDashboard.modals";
import { Modal, Padding } from "@app/containers/Modal";
import {
  clearProcessedHoliday,
  updateProcessedHoliday,
} from "@app/redux/holidays/holidays.actions";
import { getProcessedHoliday } from "@app/redux/holidays/holidays.selectors";
import { useModalStore } from "@app/hooks/useModalStore";

enum FIELDS {
  NEED_CLEANING_FOR_HOLIDAYS = "need-cleaning-for-holidays",
}

const radioConfig = [
  {
    value: false,
    name: FIELDS.NEED_CLEANING_FOR_HOLIDAYS,
    id: `${FIELDS.NEED_CLEANING_FOR_HOLIDAYS}-no`,
    label: "batmaid_pwa_generic.answer_no",
    testId: HOLIDAYS.ADD_VACATION_CLEANING_RADIO_NO,
  },
  {
    value: true,
    name: FIELDS.NEED_CLEANING_FOR_HOLIDAYS,
    id: `${FIELDS.NEED_CLEANING_FOR_HOLIDAYS}-yes`,
    label: "batmaid_pwa_generic.answer_yes",
    testId: HOLIDAYS.ADD_VACATION_CLEANING_RADIO_YES,
  },
];

interface Props {
  onModalSubmit: (holidayId?: string) => void;
}

const AddVacation = (props: Props): React.ReactElement | null => {
  const { hideModal, showModal } = useModalStore();
  const translate = useTranslate();
  const dispatch = useDispatch();
  const { mobile } = useBreakpoint("v1");
  const { language } = useLocalisation();

  const processedHoliday = useSelector(getProcessedHoliday);

  const startDate = stringifyFalsies(processedHoliday?.get("startDate"));
  const endDate = stringifyFalsies(processedHoliday?.get("endDate"));
  const calendarStartDate = stringifyFalsies(
    processedHoliday?.get("calendarStartDate"),
  );
  const skipCleanings = processedHoliday?.get("skipCleanings");
  const holidayId = processedHoliday?.get("id");

  const handleModalSubmit = (): void => {
    /** Enforce date selection before modal submit action is called */
    if (!startDate && !endDate) {
      showToast(
        translate("batmaid_pwa_holidays.select_valid_date_range_warning"),
        {
          type: "dark",
        },
      );
      return;
    }

    hideModal(OPTIONS_MODALS.ADD_VACATION);

    /** Mobile flow (go to extra modal) */
    if (mobile) {
      showModal(OPTIONS_MODALS.CLEANING_FOR_HOLIDAYS);
      return;
    }

    /** Desktop flow without Pause cleanings - go directly to submit action*/
    props.onModalSubmit(holidayId);
  };

  if (!processedHoliday) {
    return null;
  }

  return (
    <Modal
      title={translate("batmaid_pwa_client_options.add_vacation_modal_header")}
      id={OPTIONS_MODALS.ADD_VACATION}
      size="large"
      centeredContent
      padding={"none" as Padding}
      submit={handleModalSubmit}
      close={() => {
        hideModal(OPTIONS_MODALS.ADD_VACATION);
        dispatch(clearProcessedHoliday());
      }}
      withMobileHeaderGutter
      withContainedHeight
      submitBtnColor="blue"
    >
      <RangePicker
        variant={THEME_VARIANT.CLIENT}
        language={language}
        monthTranslationKey={"batmaid_pwa_generic.month_"}
        initialStartDate={new Date(startDate)}
        initialEndDate={new Date(endDate)}
        calendarStartDate={new Date(calendarStartDate)}
        onDayChange={(data: RangePickerState) => {
          if (data.startDate && data.endDate) {
            dispatch(
              updateProcessedHoliday({
                startDate: dateToCalendarFormat(data.startDate),
                endDate: dateToCalendarFormat(data.endDate),
              }),
            );
          }
        }}
      />
      {/* CleaningForHolidays is made for this view in mobile */}
      {!mobile && (
        <Margins
          xs={[null, "base_x4", "base_x2", "base_x2"]}
          lg={[null, "base_x4", "base_x5", "base_x5"]}
        >
          <div>
            <form>
              <Margins xs={[null, "base", null, null]}>
                <div style={{ textAlign: "left" }}>
                  <Label>
                    {translate(
                      "batmaid_pwa_holidays.cleaning_for_holidays_question",
                    )}
                  </Label>
                </div>
              </Margins>
              <OptionHolder>
                {radioConfig.map(opt => (
                  <TestWrapper testId={opt.testId} key={opt.label}>
                    <Radio
                      label={translate(opt.label)}
                      name={FIELDS.NEED_CLEANING_FOR_HOLIDAYS}
                      onChange={(e: InputEvent) => {
                        dispatch(
                          updateProcessedHoliday({
                            skipCleanings: !stringToBool(e.currentTarget.value),
                          }),
                        );
                      }}
                      id={opt.id}
                      value={String(opt.value)}
                      checked={opt.value === !skipCleanings}
                    />
                  </TestWrapper>
                ))}
              </OptionHolder>
            </form>
          </div>
        </Margins>
      )}
    </Modal>
  );
};

export { AddVacation };

AddVacation.displayName = "AddVacation";
